import React, { useState } from "react";
import axios from "axios";
import cx from "classnames";
import moment from "moment";
import ReactTooltip from "react-tooltip";

import { openHTMLWindow } from "./utils";

/**
 * Displays a confirmation message whether to cancel a course.
 * @param {{uuid: String}} course Course object
 * @param {[{uuid: String}]} users Students in the course
 * @param {Function} onSuccess
 * @param {Function} onError
 * @param {Function} fallbackCloseDialog
 * @returns {JSX.Element}
 */
export default function CancelCourseDialog({ course, users, onSuccess, onError, fallbackCloseDialog }) {
    const [loading, setLoading] = useState(0);
    const [selectedUsers, setSelectedUsers] = useState([...users.map((u) => u.uuid)]);
    const notificationType = "course-cancelled";

    const handleToggleAll = (event) => {
        const { checked } = event.target;

        if (checked) {
            setSelectedUsers([...users.map((u) => u.uuid)]);
        } else {
            setSelectedUsers([]);
        }
    };

    function handleCancel(event) {
        event.preventDefault();
        fallbackCloseDialog();
    }

    const selectedCount = selectedUsers.length;

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading((loaders) => loaders + 1);

        axios
            .patch(`/v1/courses/${course.uuid}/`, { is_cancelled: true })
            .then((response) => {
                const returnData = {
                    course: response.data,
                    notified: 0
                };

                if (selectedUsers.length > 0) {
                    axios
                        .post(`/v1/courses/${course.uuid}/notify/`, {
                            users: selectedUsers,
                            notification_type: notificationType
                        })
                        .then((response) => {
                            returnData.notified = selectedUsers.length;
                            setLoading(0);
                            onSuccess(returnData);
                            fallbackCloseDialog();
                        })
                        .catch((error) => {
                            setLoading(0);
                            // note: we use the same onError for all errors, this might be a bit confusing
                            onError(error);
                        });
                } else {
                    setLoading(0);
                    onSuccess(returnData);
                    fallbackCloseDialog();
                }
            })
            .catch((error) => {
                setLoading(0);
                // note: we use the same onError for all errors, this might be a bit confusing
                onError(error);
                fallbackCloseDialog();
            });
    };

    const handleShowMailPreview = (event, userUUID) => {
        event.preventDefault();
        event.stopPropagation();
        openHTMLWindow(`/v1/courses/${course.uuid}/notification-templates/${notificationType}/preview/${userUUID}`).catch((error) => {
            onError(error);
        });
    };

    const handleUserSelected = (event, user) => {
        const { checked } = event.target;

        if (checked) {
            setSelectedUsers([...selectedUsers, user.uuid]);
        } else {
            const newUsersState = selectedUsers.filter((u) => u !== user.uuid);
            setSelectedUsers(newUsersState);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <p>
                Weet je zeker dat je deze cursus wilt annuleren?
                {users?.length > 0 && (
                    <>
                        <br />
                        Door deze actie worden ook de aan deze cursus gekoppelde cursisten verwijderd.
                    </>
                )}
            </p>

            {users?.length > 0 && (
                <>
                    <p>De geselecteerde cursisten zullen op de hoogte worden gebracht van de annulering van de cursus.</p>

                    <div className="rc-dialog-overflow">
                        <table className="table table-hover table-clickable">
                            <thead>
                                <tr>
                                    <th>
                                        <input type="checkbox" onChange={handleToggleAll} checked={users?.length === selectedCount} />
                                    </th>
                                    <th>NIBHV nummer</th>
                                    <th>Naam</th>
                                    <th>E-mailadres</th>
                                    <th>Laatst uitgenodigd</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((user, i) => {
                                    const isSelected = selectedUsers?.includes(user.uuid);
                                    const notified_by_tip_id = `tip-invited-by-${user.uuid}`;

                                    return (
                                        <tr key={user.uuid} className={cx({ inactive: !isSelected })}>
                                            <td>
                                                <input
                                                    type="checkbox"
                                                    onChange={(e) => handleUserSelected(e, user)}
                                                    checked={selectedUsers.includes(user.uuid)}
                                                    title="Selecteer deze cursist"
                                                />
                                            </td>
                                            <td>{user.nibhv_number}</td>
                                            <td>{user.full_name}</td>
                                            <td>{user.email}</td>
                                            <td>
                                                {user.last_notified ? (
                                                    <>
                                                        <time dateTime={user.last_notified.notified} data-tip data-for={notified_by_tip_id}>
                                                            {moment(user.last_notified.notified).format("DD-MM-YYYY HH:mm")}
                                                        </time>
                                                        <ReactTooltip id={notified_by_tip_id} effect="solid" place="bottom">
                                                            Door {user.last_notified.notified_by.full_name}
                                                        </ReactTooltip>
                                                    </>
                                                ) : (
                                                    <i className="fa fa-times" title="Gebruiker is nooit uitgenodigd" data-tip data-for="tip-notification-info" />
                                                )}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn-light fa fa-eye"
                                                    onClick={(event) => handleShowMailPreview(event, user.uuid)}
                                                    data-tip
                                                    data-for="tip-show-preview"
                                                >
                                                    Voorbeeld
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <p>
                        Als je de cursus annuleert
                        {selectedCount === 1 ? " zal er 1 cursist " : ` zullen er ${selectedCount || "geen"} cursisten `}
                        op de hoogte worden gebracht.
                    </p>
                </>
            )}

            <ReactTooltip id="tip-show-preview" effect="solid" place="left">
                <p>Toon een voorbeeld van de e-mail die naar deze gebruiker zal worden gestuurd.</p>
            </ReactTooltip>

            <div className="flex flex-justify-end">
                <button className="btn btn-secondary" onClick={handleCancel}>
                    Annuleren
                </button>
                <button type="submit" className="btn btn-primary no-sideflap" disabled={loading}>
                    Ja, ik weet het zeker
                </button>
            </div>
        </form>
    );
}
